import type { MouseEvent, ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { Bell, ChevronLeft, House, NotebookText, UsersRound, type LucideIcon } from "lucide-react";
import type { Me } from "@repo/shared-types";
import { Avatar } from "@/components/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTyping } from "@/hooks/use-typing";
import { cn } from "@/lib/utils";

/** The log is the personal space, `/t/me` and the entries under it. */
function inLog(path: string): boolean {
  return path === "/t/me" || path.startsWith("/t/me/");
}

type Tab = {
  to: string;
  label: string;
  icon?: LucideIcon;
  /** Which addresses light the tab: a page's section is its address, as a page's space is. */
  lights: (path: string) => boolean;
};

const TABS: Tab[] = [
  { to: "/", label: "Home", icon: House, lights: (path) => path === "/" },
  {
    to: "/spaces",
    label: "Spaces",
    icon: UsersRound,
    lights: (path) => path.startsWith("/spaces") || path.startsWith("/invitations/") || (path.startsWith("/t/") && !inLog(path)),
  },
  { to: "/t/me", label: "Log", icon: NotebookText, lights: inLog },
  { to: "/inbox", label: "Inbox", icon: Bell, lights: (path) => path.startsWith("/inbox") },
  { to: "/account", label: "You", lights: (path) => path.startsWith("/account") || path.startsWith("/feedback") },
];

/**
 * The tab bar's look — a capsule at its width, the page blurred behind it —
 * which the record card floating on it shares (0082).
 */
export const floatingGlass =
  "mx-auto max-w-md rounded-full border border-border bg-background/50 shadow-lg backdrop-blur-xl backdrop-saturate-150";

/**
 * The capsule floating at the bottom of every signed-in page — how a phone
 * app moves between its sections (decided with Tiago 10 October) — over the
 * page, just above the home indicator, with a grey pill behind the open tab
 * that slides to the next one (0082). Where it sits is
 * `--tab-bar-gap` in `src/index.css`; whatever floats above it is placed
 * from `--tab-bar-top`, the capsule's top.
 * "You" is the viewer's own picture, which also says whose account this is.
 * It steps aside while somebody types on a touch screen, which Safari would
 * otherwise leave floating above the keyboard (ADR 0071).
 */
export function TabBar({ session, unread }: { session: Me; unread: number }) {
  const path = useLocation().pathname;
  const typing = useTyping();
  if (typing) return null;
  const open = TABS.findIndex((tab) => tab.lights(path));
  return (
    <nav aria-label="Main" className="pointer-events-none fixed inset-x-0 bottom-(--tab-bar-gap) z-30 px-4">
      <div className={cn(floatingGlass, "pointer-events-auto relative h-(--tab-bar-height) p-1.5")}>
        {/* One pill, moved under the open tab rather than drawn in it, so it
            slides from tab to tab — and drawn in place when coming from a page
            no tab lights. Its width is a tab's: the capsule less its p-1.5. */}
        {open >= 0 && (
          <span
            aria-hidden="true"
            className="absolute inset-y-1.5 left-1.5 rounded-full bg-foreground/10 motion-safe:transition-[translate] motion-safe:duration-(--motion-slide) motion-safe:ease-out"
            style={{ width: `calc((100% - 0.75rem) / ${TABS.length})`, translate: `${open * 100}% 0` }}
          />
        )}
        <ul className="relative flex h-full">
          {TABS.map((tab, index) => {
            const lit = index === open;
            const Icon = tab.icon;
            return (
              <li key={tab.to} className="flex-1">
                <Link
                  to={tab.to}
                  aria-current={lit ? "page" : undefined}
                  className={cn(
                    "relative flex h-full flex-col items-center justify-center gap-0.5 rounded-full text-[11px] font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                    lit ? "text-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {Icon ? (
                    <Icon aria-hidden="true" size={24} strokeWidth={lit ? 2.2 : 1.8} />
                  ) : (
                    <Avatar person={session.user} size="sm" className={lit ? "ring-2 ring-foreground" : ""} />
                  )}
                  {tab.label}
                  {tab.to === "/inbox" && unread > 0 && (
                    <Badge
                      variant="destructive"
                      aria-label={`${unread} unread`}
                      className="absolute top-1 left-1/2 ml-1.5 h-[18px] min-w-[18px] bg-destructive px-1.5 text-[11px] text-white"
                    >
                      {unread}
                    </Badge>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}

/**
 * A page's first row, one height on every page (0078): a tab's title, or
 * the way back, and the round icons that act on the page — Home's eye, a
 * cog — never a button that makes something (0079). The height is
 * `BackLink`'s, so a page with the way back alone needs no bar around it.
 */
export function TopBar({ children }: { children: ReactNode }) {
  return <div className="flex h-11 items-center justify-between gap-3">{children}</div>;
}

/**
 * The way back to where a page sits — its space, its event, the log — at the
 * top left, as a phone app puts it. A screen reader hears where it goes. A
 * long name is cut short rather than pushing what shares the bar off a
 * phone's screen. Without a place to name — `children` of "Back" — it says
 * just that.
 */
export function BackLink({
  to,
  onClick,
  children,
}: {
  to: string;
  /** A way back that is Back, where the page knows it came from `to`. */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  children: ReactNode;
}) {
  return (
    <Button asChild variant="ghost" className="-ml-3 h-11 min-w-0 gap-0.5 pr-3 pl-1.5">
      <Link to={to} onClick={onClick}>
        <ChevronLeft aria-hidden="true" className="size-5" />
        <span className="truncate">
          {children === "Back" ? (
            children
          ) : (
            <>
              <span className="sr-only">Back to</span> {children}
            </>
          )}
        </span>
      </Link>
    </Button>
  );
}

/**
 * A link to one of Account's pages from elsewhere — "Set your division in
 * Account" — carrying where it was followed from, so that page's way back
 * returns there rather than to the Account list (ADR 0068).
 */
export function useAccountLink(page: "profile" | "division"): string {
  const { pathname, search } = useLocation();
  return `/account/${page}?${new URLSearchParams({ from: pathname + search })}`;
}
